/**
 * usePermissions — Hook centralizado para verificação de permissões no frontend
 *
 * Calcula as permissões do usuário com base no perfil, cargo e preferências granulares,
 * usando a engine oficial sincronizada com o backend (permissions.ts).
 *
 * @example
 * ```tsx
 * const { hasPermission, canManage, canView, isAdmin, isDiretor } = usePermissions();
 *
 * // Esconder botão de delete se não tem permissão
 * {hasPermission('alunos.manage') && <button onClick={handleDelete}>Excluir</button>}
 *
 * // Shorthand para domínio
 * {canManage('alunos') && <DeleteButton />}
 * {canView('turmas') && <TurmasList />}
 * ```
 */
import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  ALL_PERMISSIONS,
  type PermissionKey,
  type PermissionMap,
  resolvePermissions,
  normalizeEscolaCargo,
  isEscolaDiretorCargo,
} from '../auth/permissions';

import { toast } from 'sonner';

export { ALL_PERMISSIONS as ALL_PERMISSION_KEYS };
export type { PermissionKey, PermissionMap };

/**
 * Executa uma ação somente se tiver permissão; caso contrário, exibe toast amigável e estilo desabilitado.
 */
export function handlePermissionClick(
  hasPerm: boolean,
  action: () => void,
  customMsg?: string,
) {
  if (!hasPerm) {
    toast.error(
      customMsg || 'Você não tem permissão para realizar esta ação. Solicite acesso ao Diretor ou Administrador da escola.',
    );
    return;
  }
  action();
}

export interface UsePermissionsReturn {
  /** Mapa completo de permissões calculadas com base no perfil, cargo e preferências granulares */
  permissions: PermissionMap;
  /** Verifica se possui uma permissão específica */
  hasPermission: (key: PermissionKey | string) => boolean;
  /** Verifica se pode gerenciar um domínio (atalho para {domain}.manage) */
  canManage: (domain: string) => boolean;
  /** Verifica se pode visualizar um domínio (atalho para {domain}.view) */
  canView: (domain: string) => boolean;
  /** Executa ação se permitido ou exibe aviso de permissão */
  guardAction: (domainOrKey: string, action: () => void, customMsg?: string) => void;
  /** true se o usuário é ADMIN / MASTER */
  isAdmin: boolean;
  /** true se o usuário é DIRETOR ou VICE-DIRETOR */
  isDiretor: boolean;
  /** Perfil do usuário */
  perfil: string | null;
  /** Cargo do usuário (para ESCOLA) */
  cargo: string | null;
}

export function usePermissions(): UsePermissionsReturn {
  const user = useAuthStore((state) => state.user);
  const perfil = user?.perfil || null;
  const cargo = user?.cargo || null;

  const permissions = useMemo(() => resolvePermissions(user), [user]);

  const hasPermission = (key: PermissionKey | string): boolean => {
    return permissions[key as PermissionKey] === true;
  };

  const canManage = (domain: string): boolean => {
    const key = `${domain}.manage` as PermissionKey;
    return permissions[key] === true;
  };

  const canView = (domain: string): boolean => {
    const key = `${domain}.view` as PermissionKey;
    return permissions[key] === true;
  };

  const guardAction = (domainOrKey: string, action: () => void, customMsg?: string) => {
    const isFullKey = domainOrKey.includes('.');
    const perm = isFullKey ? hasPermission(domainOrKey) : canManage(domainOrKey);
    handlePermissionClick(perm, action, customMsg);
  };

  const p = (perfil || '').toUpperCase();
  const isAdmin = p === 'ADMIN' || p === 'MASTER' || p === 'SISTEMA';
  const isDiretor = (p === 'ESCOLA' && (!cargo || isEscolaDiretorCargo(cargo))) || isAdmin;

  return {
    permissions,
    hasPermission,
    canManage,
    canView,
    guardAction,
    isAdmin,
    isDiretor,
    perfil,
    cargo,
  };
}
